import { useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Mail, Users } from 'lucide-react';
import { Button } from '@/components/ui';
import { SelectionCard } from '@/components/domain/onboarding';
import type { EntryChoice } from '@/stores/onboardingStore';
import { OnboardingLayout } from './OnboardingLayout';
import { ONBOARDING_COPY } from './onboardingCopy';

const CHOICES: readonly EntryChoice[] = ['admin', 'helper'];

export interface ForkScreenProps {
  /** Commits the choice. Rejecting shows the error line and keeps the selection. */
  onContinue: (choice: EntryChoice) => Promise<void>;
  /** The chevron. On the fork there is nowhere back to go, so the parent opens sign-out. */
  onBack: () => void;
}

/**
 * The create-vs-join fork that follows sign-in (YAR-438 / YAR-439, Figma
 * "Fork / default" 10629:35). Renders only when the parent is signed in, has
 * no pending invite and has not finished onboarding — that gate is
 * `Onboarding.tsx`'s, not this screen's.
 *
 * Presentational on purpose: the choice is local state until Continue, and
 * what Continue does (create the household, or nothing at all for a helper)
 * is the container's — Kelvin's note: "Continue commits the selection".
 * Sits outside the onboarding step count, so no progress bar.
 *
 * The two cards form a single WAI-ARIA radio group: the group (not the
 * cards) owns roving `tabIndex` and the arrow keys, so Tab enters the group
 * once and Up/Left and Down/Right move both the checked card and focus
 * between the two, wrapping at the ends. Space/Enter stay the card's own.
 */
export function ForkScreen({ onContinue, onBack }: ForkScreenProps) {
  const copy = ONBOARDING_COPY.fork;
  const [selected, setSelected] = useState<EntryChoice | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cardRefs = useRef<Partial<Record<EntryChoice, HTMLDivElement | null>>>(
    {},
  );

  const handleContinue = async () => {
    if (!selected || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onContinue(selected);
    } catch {
      setError(copy.error);
    } finally {
      setBusy(false);
    }
  };

  const handleGroupKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const current = selected ?? CHOICES[0];
    const currentIndex = CHOICES.indexOf(current);
    let nextIndex: number | null = null;
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % CHOICES.length;
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + CHOICES.length) % CHOICES.length;
    }
    if (nextIndex === null) return;
    e.preventDefault();
    const next = CHOICES[nextIndex];
    setSelected(next);
    cardRefs.current[next]?.focus();
  };

  return (
    <OnboardingLayout
      showBackButton
      onBack={onBack}
      showProgressBar={false}
      stickyFooter={
        <div className="flex flex-col gap-2">
          {error && (
            <p
              role="alert"
              className="text-xs text-text-error"
            >
              {error}
            </p>
          )}
          <Button
            variant="primary"
            fullWidth
            disabled={!selected || busy}
            loading={busy}
            onClick={() => void handleContinue()}
          >
            {copy.cta}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-10">
        <h1 className="text-[28px] leading-8 font-bold text-text-hero">
          {copy.title}
        </h1>
        <div
          role="radiogroup"
          aria-label={copy.title}
          className="flex flex-col gap-5"
          onKeyDown={handleGroupKeyDown}
        >
          <SelectionCard
            id="admin"
            ref={(el) => {
              cardRefs.current.admin = el;
            }}
            icon={<Mail aria-hidden />}
            label={copy.options.admin.label}
            checks={copy.options.admin.checks}
            selected={selected === 'admin'}
            onSelect={() => setSelected('admin')}
            tabIndex={(selected ?? CHOICES[0]) === 'admin' ? 0 : -1}
          />
          <SelectionCard
            id="helper"
            ref={(el) => {
              cardRefs.current.helper = el;
            }}
            icon={<Users aria-hidden />}
            label={copy.options.helper.label}
            checks={copy.options.helper.checks}
            selected={selected === 'helper'}
            onSelect={() => setSelected('helper')}
            tabIndex={(selected ?? CHOICES[0]) === 'helper' ? 0 : -1}
          />
        </div>
      </div>
    </OnboardingLayout>
  );
}
